<script setup lang="ts">
import type { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { object, number, string } from 'yup';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { salariesApi, type Salary, type SalaryForm } from '@/api/salaries';
import { usePermissions } from '@/composables/usePermissions';
import PriceInput from '@/components/price-input.vue';

const props = defineProps<{
  caregiverId: number;
  salary?: Salary | null;
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const { t } = useI18n();
const { hasPermission } = usePermissions();
const queryClient = useQueryClient();

const isEditMode = computed(() => !!props.salary?.id);
const formTitle = computed(() => {
  if (isEditMode.value) return t('salaryForm.editTitle');
  return t('salaryForm.createTitle');
});
const canSave = computed(() => {
  const action = isEditMode.value ? 'update' : 'create';
  return hasPermission.value('service_prices', action);
});

const validationSchema = object({
  baseSalary: number()
    .required(t('salaryForm.fieldRequired'))
    .typeError(t('salaryForm.fieldPositiveNumber'))
    .min(0, t('salaryForm.fieldPositiveNumber')),
  amountPerShift: number()
    .required(t('salaryForm.fieldRequired'))
    .typeError(t('salaryForm.fieldPositiveNumber'))
    .min(0, t('salaryForm.fieldPositiveNumber')),
  minimumShiftsThreshold: number()
    .required(t('salaryForm.fieldRequired'))
    .typeError(t('salaryForm.fieldInteger'))
    .min(0, t('salaryForm.fieldNonNegativeInteger'))
    .integer(t('salaryForm.fieldInteger')),
  startsOn: string()
    .required(t('salaryForm.fieldRequired')),
  endsOn: string()
    .nullable()
    .test('after-starts-on', t('salaryForm.endsOnAfterStartsOn'), function (value) {
      if (!value) return true;
      const { startsOn } = this.parent;
      if (!startsOn) return true;
      return new Date(value) > new Date(startsOn);
    }),
});

const { handleSubmit, errors, defineField, setValues } = useForm({
  validationSchema,
});

const [baseSalary, baseSalaryAttrs] = defineField('baseSalary');
const [amountPerShift, amountPerShiftAttrs] = defineField('amountPerShift');
const [minimumShiftsThreshold, minimumShiftsThresholdAttrs] = defineField('minimumShiftsThreshold');
const [startsOn, startsOnAttrs] = defineField('startsOn');
const [endsOn, endsOnAttrs] = defineField('endsOn');

function getDefaultValues() {
  return {
    baseSalary: 0,
    amountPerShift: 0,
    minimumShiftsThreshold: 0,
    startsOn: new Date().toISOString().split('T')[0],
    endsOn: null,
  };
}

function getSalaryValues(salary: Salary) {
  return {
    baseSalary: salary.baseSalary,
    amountPerShift: salary.amountPerShift,
    minimumShiftsThreshold: salary.minimumShiftsThreshold,
    startsOn: salary.startsOn,
    endsOn: salary.endsOn,
  };
}

watch(
  () => props.salary,
  (salary) => {
    const values = salary ? getSalaryValues(salary) : getDefaultValues();
    setValues(values);
  },
  { immediate: true },
);

const {
  mutate: createSalary,
  isPending: isCreatePending,
  isError: isCreateError,
} = useMutation<Salary, AxiosError, SalaryForm>({
  mutationFn: (data: SalaryForm) => salariesApi.create(data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['salaries', props.caregiverId] });
    queryClient.invalidateQueries({ queryKey: ['caregiver', props.caregiverId] });
    emit('saved');
  },
});

const {
  mutate: updateSalary,
  isPending: isUpdatePending,
  isError: isUpdateError,
} = useMutation<Salary, AxiosError, Partial<SalaryForm>>({
  mutationFn: (data: Partial<SalaryForm>) => salariesApi.update(data),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['salaries', props.caregiverId] });
    queryClient.invalidateQueries({ queryKey: ['caregiver', props.caregiverId] });
    emit('saved');
  },
});

const isPending = computed(() => isCreatePending.value || isUpdatePending.value);
const isError = computed(() => isCreateError.value || isUpdateError.value);

const onSubmit = handleSubmit((values) => {
  const formData: SalaryForm = {
    caregiverId: props.caregiverId,
    baseSalary: values.baseSalary,
    amountPerShift: values.amountPerShift,
    minimumShiftsThreshold: values.minimumShiftsThreshold,
    startsOn: values.startsOn,
    endsOn: values.endsOn || null,
  };

  if (isEditMode.value && props.salary?.id) {
    updateSalary({ ...formData, id: props.salary.id });
  } else {
    createSalary(formData);
  }
});
</script>

<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">{{ formTitle }}</span>
    </v-card-title>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <price-input
              v-model="baseSalary"
              v-bind="baseSalaryAttrs"
              :label="t('salaryForm.baseSalary')"
              :error-messages="errors.baseSalary"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <price-input
              v-model="amountPerShift"
              v-bind="amountPerShiftAttrs"
              :label="t('salaryForm.amountPerShift')"
              :error-messages="errors.amountPerShift"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model.number="minimumShiftsThreshold"
              v-bind="minimumShiftsThresholdAttrs"
              :label="t('salaryForm.minimumShiftsThreshold')"
              type="number"
              min="0"
              step="1"
              variant="outlined"
              :error-messages="errors.minimumShiftsThreshold"
              :hint="t('salaryForm.minimumShiftsThresholdHint')"
              persistent-hint
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="startsOn"
              v-bind="startsOnAttrs"
              :label="t('salaryForm.startsOn')"
              type="date"
              variant="outlined"
              :error-messages="errors.startsOn"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model="endsOn"
              v-bind="endsOnAttrs"
              :label="t('salaryForm.endsOn')"
              type="date"
              variant="outlined"
              clearable
              :error-messages="errors.endsOn"
              :hint="t('salaryForm.endsOnHint')"
              persistent-hint
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-btn
        variant="text"
        @click="emit('close')"
      >
        {{ t('salaryForm.cancel') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="canSave"
        color="primary"
        variant="text"
        :loading="isPending"
        @click="onSubmit"
      >
        {{ t('salaryForm.save') }}
      </v-btn>
    </v-card-actions>
    <v-alert
      v-if="isError"
      class="mt-4 rounded"
      type="error"
    >
      {{ t('salaryForm.saveError') }}
    </v-alert>
  </v-card>
</template>
